import { useState } from "react";
import { Button } from "@/components/ui/button";
import { GlassCard, GlassCardContent, GlassCardDescription, GlassCardHeader, GlassCardTitle } from "@/components/ui/glass-card";
import { Loader2, Sparkles, Download } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { MemojiOptionGrid } from "./memoji/MemojiOptionGrid";
import { memojiOptions } from "./memoji/memojiOptions";

interface CustomMemojiCreatorProps {
  onAvatarCreated: (imageUrl: string) => void;
  onCancel: () => void;
}

interface MemojiCustomization {
  gender?: string;
  ageGroup?: string;
  skinTone?: string;
  hairStyle?: string;
  hairColor?: string;
  eyeColor?: string;
  facialHair?: string;
  glasses?: string;
  hat?: string;
  expression?: string;
}

export const CustomMemojiCreator = ({ onAvatarCreated, onCancel }: CustomMemojiCreatorProps) => {
  const [customization, setCustomization] = useState<MemojiCustomization>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const updateCustomization = (key: keyof MemojiCustomization, value: string) => {
    setCustomization(prev => ({ ...prev, [key]: value }));
  };

  const generateCustomMemoji = async () => {
    try {
      setIsGenerating(true);
      
      const { data, error } = await supabase.functions.invoke('generate-custom-memoji', {
        body: { customization }
      });

      if (error) throw error;

      if (data.success && data.imageUrl) {
        setPreviewUrl(data.imageUrl);
        toast.success("Custom Memoji generated successfully!");
      } else {
        throw new Error("Failed to generate Memoji");
      }
    } catch (error) {
      console.error("Error generating custom Memoji:", error);
      toast.error("Failed to generate custom Memoji. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const useGeneratedAvatar = () => {
    if (previewUrl) {
      onAvatarCreated(previewUrl);
    }
  };

  return (
    <div className="space-y-6">
      <GlassCard variant="default">
        <GlassCardHeader className="text-center pb-2">
          <GlassCardTitle className="flex items-center justify-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Create Your Memoji
            </GlassCardTitle>
          <GlassCardDescription>
            Customize your Apple-style avatar
          </GlassCardDescription>
        </GlassCardHeader>
        
        {/* Live Preview Section */}
        <div className="px-6 pb-4">
          <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-background rounded-2xl p-8 text-center">
            {previewUrl ? (
              <div className="inline-block relative">
                <img
                  src={previewUrl}
                  alt="Your Memoji Preview"
                  className="w-32 h-32 rounded-full border-4 border-white shadow-lg mx-auto"
                />
                <div className="absolute -bottom-2 -right-2 bg-primary text-primary-foreground rounded-full p-1">
                  <Sparkles className="h-4 w-4" />
                </div>
              </div>
            ) : (
              <div className="w-32 h-32 rounded-full border-4 border-dashed border-muted-foreground/30 mx-auto flex items-center justify-center bg-muted/20">
                <div className="text-center">
                  <Sparkles className="h-8 w-8 text-muted-foreground/50 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Your Memoji will appear here</p>
                </div>
              </div>
            )}
          </div>
        </div>
        <GlassCardContent className="space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Gender */}
          <MemojiOptionGrid
            title="Gender"
            options={memojiOptions.gender}
            selectedValue={customization.gender}
            onSelect={(value) => updateCustomization('gender', value)}
          />

          {/* Age Group */}
          <MemojiOptionGrid
            title="Age Group"
            options={memojiOptions.ageGroup}
            selectedValue={customization.ageGroup}
            onSelect={(value) => updateCustomization('ageGroup', value)}
          />

          {/* Skin Tone */}
          <MemojiOptionGrid
            title="Skin Tone"
            options={memojiOptions.skinTone}
            selectedValue={customization.skinTone}
            onSelect={(value) => updateCustomization('skinTone', value)}
          />

          {/* Hair Style */}
          <MemojiOptionGrid
            title="Hair Style"
            options={memojiOptions.hairStyle}
            selectedValue={customization.hairStyle}
            onSelect={(value) => updateCustomization('hairStyle', value)}
          />

          {/* Hair Color */}
          <MemojiOptionGrid
            title="Hair Color"
            options={memojiOptions.hairColor}
            selectedValue={customization.hairColor}
            onSelect={(value) => updateCustomization('hairColor', value)}
          />

          {/* Eye Color */}
          <MemojiOptionGrid
            title="Eye Color"
            options={memojiOptions.eyeColor}
            selectedValue={customization.eyeColor}
            onSelect={(value) => updateCustomization('eyeColor', value)}
          />

          {/* Facial Hair */}
          <MemojiOptionGrid
            title="Facial Hair"
            options={memojiOptions.facialHair}
            selectedValue={customization.facialHair}
            onSelect={(value) => updateCustomization('facialHair', value)}
          />

          {/* Glasses */}
          <MemojiOptionGrid
            title="Glasses"
            options={memojiOptions.glasses}
            selectedValue={customization.glasses}
            onSelect={(value) => updateCustomization('glasses', value)}
          />

          {/* Expression */}
          <MemojiOptionGrid
            title="Expression"
            options={memojiOptions.expression}
            selectedValue={customization.expression}
            onSelect={(value) => updateCustomization('expression', value)}
          />

          <div className="flex gap-2 pt-4">
            <Button
              onClick={generateCustomMemoji}
              disabled={isGenerating || !customization.gender}
              className="flex-1"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate My Memoji
                </>
              )}
            </Button>
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>

          {/* Action Buttons */}
          {previewUrl && (
            <div className="space-y-3 pt-4 border-t">
              <div className="flex gap-2">
                <Button onClick={useGeneratedAvatar} className="flex-1 bg-primary hover:bg-primary/90">
                  <Download className="h-4 w-4 mr-2" />
                  Use This Memoji
                </Button>
                <Button variant="outline" onClick={generateCustomMemoji} disabled={isGenerating}>
                  {isGenerating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Try Again"
                  )}
                </Button>
              </div>
            </div>
          )}
        </GlassCardContent>
      </GlassCard>
    </div>
  );
};